/**
 * OrderReviews Component
 *
 * Container component that fetches and displays reviews for an order
 * Shows:
 * - Both reviews (client→worker and worker→client) when published
 * - Call-to-action to leave review if pending
 * - Status messages (waiting for other party, etc.)
 */

'use client';

import { FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { PrimaryButton } from '@/src/shared/ui/button/PrimaryButton';
import { Icon } from '@/src/shared/ui/icons/Icon';
import { reviewsApi } from '@/src/shared/api/reviews.api';
import { useModal } from '@/src/shared/hooks/useModal';
import { OrderReviewCard } from './OrderReviewCard';
import { cn } from '@/src/shared/lib/utils';

interface OrderReviewsProps {
  orderId: string;
  userRole: 'client' | 'worker';
  orderTitle?: string;
  workerName?: string;
  workerAvatarUrl?: string;
  clientName?: string;
  clientAvatarUrl?: string;
  className?: string;
  showExpanded?: boolean; // Show reviews expanded by default (for modal)
  onReviewCreated?: () => void;
}

/**
 * Calculate days remaining until review deadline
 */
function getDaysRemaining(completedAt: string | null): number | null {
  if (!completedAt) return null;

  const completed = new Date(completedAt);
  const deadline = new Date(completed.getTime() + 14 * 24 * 60 * 60 * 1000); // 14 days
  const now = new Date();
  const daysRemaining = Math.ceil((deadline.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));

  return Math.max(0, daysRemaining);
}

/**
 * OrderReviews - displays reviews for an order with CTA buttons
 */
export const OrderReviews: FC<OrderReviewsProps> = ({
  orderId,
  userRole,
  orderTitle,
  workerName,
  workerAvatarUrl,
  clientName,
  clientAvatarUrl,
  className,
  showExpanded = false,
  onReviewCreated,
}) => {
  const t = useTranslations('reviews');
  const { open: openReviewModal } = useModal('create-review');

  // Fetch order reviews
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['order-reviews', orderId],
    queryFn: () => reviewsApi.getOrderReviews(orderId),
  });

  const handleLeaveReview = () => {
    openReviewModal({
      orderId,
      orderTitle,
      reviewerRole: userRole, // 'client' or 'worker'
      revieweeName: userRole === 'client' ? workerName : clientName, // Who is being reviewed
      revieweeAvatarUrl: userRole === 'client' ? workerAvatarUrl : clientAvatarUrl,
      // Deprecated props for backward compatibility
      workerName,
      workerAvatarUrl,
      onSuccess: () => {
        refetch();
        onReviewCreated?.();
      },
    });
  };

  const handleEditReview = (reviewId: string) => {
    // Find the review to edit
    const reviewToEdit = data?.reviews.find((r) => r.id === reviewId);
    if (!reviewToEdit) return;

    // Open create-review modal in edit mode
    openReviewModal({
      reviewId, // Presence of reviewId triggers edit mode
      orderId,
      orderTitle,
      reviewerRole: userRole,
      revieweeName: userRole === 'client' ? workerName : clientName,
      revieweeAvatarUrl: userRole === 'client' ? workerAvatarUrl : clientAvatarUrl,
      initialData: {
        rating: reviewToEdit.averageRating,
        comment: reviewToEdit.comment || '',
        criteriaRatings: reviewToEdit.criteriaRatings,
      },
      onSuccess: () => {
        refetch();
        onReviewCreated?.();
      },
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center p-8', className)}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-primary" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={cn('p-5 bg-red-50 border border-red-200 rounded-[20px]', className)}>
        <Typography variant="body" color="primary" className="text-red-800">
          {t('orderReview.errorLoading')}
        </Typography>
      </div>
    );
  }

  const { reviews, order, canReview } = data || { reviews: [], order: null, canReview: false };
  const daysRemaining = getDaysRemaining(order?.completedAt || null);

  // No reviews yet - only client can leave review
  if (reviews.length === 0) {
    // Worker cannot leave reviews
    if (userRole === 'worker') {
      return (
        <div className={cn('p-5 bg-background-secondary rounded-[20px]', className)}>
          <Typography variant="body" color="secondary" className="text-center">
            {t('orderReview.noReviews')}
          </Typography>
        </div>
      );
    }

    if (!canReview) {
      // Deadline passed or already reviewed
      return (
        <div className={cn('p-5 bg-background-secondary rounded-[20px]', className)}>
          <Typography variant="body" color="secondary" className="text-center">
            {t('orderReview.noReviews')}
          </Typography>
        </div>
      );
    }

    // Client can review - show CTA
    return (
      <div className={cn('p-5 bg-background-primary rounded-[20px] border border-accent-secondary', className)}>
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="w-12 h-12 rounded-full bg-accent-secondary flex items-center justify-center">
            <Icon name="star" size="lg" className="text-accent-primary" />
          </div>
          <div>
            <Typography variant="h3" weight="bold" color="primary" className="mb-2">
              {t('orderReview.leaveReviewTitle')}
            </Typography>
            <Typography variant="body" color="secondary">
              {t('orderReview.leaveReviewDescClient', { name: workerName || 'worker' })}
            </Typography>
            {daysRemaining !== null && (
              <Typography variant="bodySm" color="tertiary" className="mt-2">
                {t('orderReview.daysRemaining', { days: daysRemaining })}
              </Typography>
            )}
          </div>
          <PrimaryButton onClick={handleLeaveReview} className="w-full max-w-xs">
            <Icon name="star" size="sm" className="mr-2" />
            {t('orderReview.leaveReviewButton')}
          </PrimaryButton>
        </div>
      </div>
    );
  }

  // Show client review (client-only system)
  const clientReview = reviews.find((r) => r.reviewerActor?.id === order?.clientActor?.id);

  if (!clientReview) {
    return (
      <div className={cn('p-5 bg-background-secondary rounded-[20px]', className)}>
        <Typography variant="body" color="secondary" className="text-center">
          {t('orderReview.noReviews')}
        </Typography>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <OrderReviewCard
        review={clientReview}
        viewerRole={userRole}
        isYourReview={userRole === 'client'}
        defaultExpanded={showExpanded}
        onEdit={userRole === 'client' ? handleEditReview : undefined}
      />
    </div>
  );
};
